import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { buildUpdateScriptUrl } from "utils/updateScriptApi";
import { showMessage } from "../common.slice";
import { toScriptsUpdateApiBody, toEditScriptApiBody } from "utils/scriptFieldUtils";

/** Unified Scripts API payloads (supports legacy row shapes). */
const toScriptsUpdateBody = toScriptsUpdateApiBody;
const toEditScriptBody = toEditScriptApiBody;

const getFriendlyError = (err) => {
  const raw = err.response?.data?.message || err.response?.data || err.message || "";

  if (raw.toLowerCase().includes("int")) return "Invalid number entered.";
  if (raw.toLowerCase().includes("required")) return "Please fill required fields.";

  return "Something went wrong. Try again.";
};

export const createScript = createAsyncThunk(
  "updateScript/create",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const body = toScriptsUpdateBody(data);
      const res = await axios.post(buildUpdateScriptUrl("settings/create-script"), body);

      dispatch(
        showMessage({
          message: res.data || "Created successfully",
          varient: "success",
        })
      );

      return res.data;
    } catch (err) {
      console.error("CREATE ERROR:", err.response?.data || err.message);

      const msg = getFriendlyError(err);

      dispatch(showMessage({ message: msg, varient: "error" }));

      return rejectWithValue(msg);
    }
  }
);

export const getScriptFromDB = createAsyncThunk(
  "updateScript/getFromDB",
  async ({ pageNumber = 1, pageSize = 10, search = "" }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        buildUpdateScriptUrl(
          `settings/scripts?pageNumber=${pageNumber}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`
        )
      );

      const payload = res.data.data || res.data || {};
      return {
        records: payload.records || [],
        totalRecords: payload.totalRecords || 0,
        pageNumber: payload.pageNumber || 1,
        pageSize: payload.pageSize || 10,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateScript = createAsyncThunk(
  "updateScript/update",
  async (row, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(buildUpdateScriptUrl("settings/script-action"), {
        action: "EDIT",
        rows: [toEditScriptBody(row)],
      });

      dispatch(showMessage({ message: "Updated!", varient: "success" }));
      return res.data;
    } catch (err) {
      console.error(err.response?.data);
      dispatch(showMessage({ message: "Update failed", varient: "error" }));
      return rejectWithValue(err);
    }
  }
);

export const deleteScript = createAsyncThunk(
  "updateScript/delete",
  async (row, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(buildUpdateScriptUrl("settings/script-action"), {
        action: "DELETE",
        ids: [row.id ?? row.Id],
      });

      dispatch(showMessage({ message: "Deleted!", varient: "success" }));
      return res.data;
    } catch (err) {
      console.error(err.response?.data);
      dispatch(showMessage({ message: "Delete failed", varient: "error" }));
      return rejectWithValue(err);
    }
  }
);

export const viewScript = createAsyncThunk(
  "updateScript/view",
  async (row, { rejectWithValue }) => {
    try {
      const res = await axios.post(buildUpdateScriptUrl("settings/script-action"), {
        action: "VIEW",
        ids: [row.id ?? row.Id],
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const saveUpdateScriptData = createAsyncThunk(
  "updateScript/save",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const list = Array.isArray(data) ? data.map(toScriptsUpdateBody) : data;
      const response = await axios.post(buildUpdateScriptUrl("settings/save-script-data"), list);

      const payload = response.data;
      const main =
        typeof payload === "string"
          ? payload
          : payload?.message || "Saved successfully!";
      const hint = typeof payload === "object" && payload?.hint ? ` ${payload.hint}` : "";
      dispatch(showMessage({ message: `${main}${hint}`, varient: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showMessage({ message: "Save failed!", varient: "error" }));
      return rejectWithValue(err);
    }
  }
);

const initialState = {
  updateScriptData: [],
  dbScriptList: [],
  totalRecords: 0,
  pageNumber: 1,
  pageSize: 10,
  isSaving: false,
};

const slice = createSlice({
  name: "updateScript",
  initialState,
  reducers: {
    setUpdateScriptData: (state, action) => {
      state.updateScriptData = action.payload;
    },

    editScriptField: (state, action) => {
      const { index, field, value } = action.payload;
      const newData = [...state.updateScriptData];
      newData[index] = { ...newData[index], [field]: value };
      state.updateScriptData = newData;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScriptFromDB.fulfilled, (state, action) => {
        state.dbScriptList = action.payload.records || [];
        state.totalRecords = action.payload.totalRecords || 0;
        state.pageNumber = action.payload.pageNumber || 1;
        state.pageSize = action.payload.pageSize || 10;
      })

      .addCase(saveUpdateScriptData.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(saveUpdateScriptData.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(saveUpdateScriptData.rejected, (state) => {
        state.isSaving = false;
      })

      .addCase(updateScript.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateScript.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(updateScript.rejected, (state) => {
        state.isSaving = false;
      })

      .addCase(deleteScript.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(deleteScript.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(deleteScript.rejected, (state) => {
        state.isSaving = false;
      });
  },
});

export const { setUpdateScriptData, editScriptField } = slice.actions;
export default slice.reducer;
